import { useState, useMemo, useRef, useCallback, useEffect, FC } from 'react';
import {
    Button,
    Icons,
    ButtonStyles,
    Alert,
    Search,
    Table,
    EmptyStateIcons,
    SelectList,
    showAlert,
    Icon
} from '@silverfort/sf-components';
import useGetColumns from '../columns/useGetColumns';
import ExposuresFilters from '../Filters/ExposuresFilters';
import useGetRefSize from '../../../../hooks/useGetRefSize';
import StaticExposureConfigurationDrawer from './StaticExposureConfigurationDrawer';
import CustomExposureConfigurationDrawer from './CustomExposureConfigurationDrawer';
import { ExposureConfig, StaticExposureCustomConfig, CustomExposureConfig, SaveExposuresConfigPayload } from '../types';
import { buildDefaultCustomExposureConfig } from '../utils';
import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import useSaveExposuresConfiguration from '../../api/mutations/useSaveExposuresConfiguration';
import useSaveExposuresConfigurationStatus from '../../api/mutations/useSaveExposuresConfigurationStatus';
import useDeleteCustomExposure from '../../api/mutations/useDeleteCustomExposures';
import { useIspmContext } from '../../IspmContext';
import { useTowerLayout } from '../../../../TowerLayout/TowerLayoutProvider';

const ALL_CATEGORY = 'All';

const successMessage = 'Configuration saved successfully';
const errorMessage = 'Failed to save configuration';

const deleteSuccessMessage = 'Configuration deleted successfully';
const deleteErrorMessage = 'Failed to delete configuration';

const CREATE_CUSTOM_DROPDOWN_OPTIONS = [
    {
        id: 'user-exposure',
        title: 'User Exposure',
        value: AD_ENTITY_TYPES.USER
    },
    {
        id: 'server-exposure',
        title: 'Server Exposure',
        value: AD_ENTITY_TYPES.SERVER
    }
];

type DrawerMode = 'static' | 'create-custom' | 'edit-custom' | null;

interface ExposuresConfigurationPageProps {
    onBackClick: () => void;
    exposuresConfigurations: ExposureConfig[];
    refetchSettings: () => void;
    isFetching: boolean;
}

const ExposuresConfigurationPage: FC<ExposuresConfigurationPageProps> = ({
    onBackClick,
    exposuresConfigurations,
    refetchSettings,
    isFetching = false
}) => {
    const tableRef = useRef(null);
    const { height: tableHeight } = useGetRefSize(tableRef);
    const { refreshInsightsData } = useIspmContext();
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string[] | undefined>([]);
    const [showOnlyActive, setShowOnlyActive] = useState(false);
    const [drawerMode, setDrawerMode] = useState<DrawerMode>(null);
    const [selectedExposure, setSelectedExposure] = useState<ExposureConfig | CustomExposureConfig | null>(null);
    const [newCustomEntityType, setNewCustomEntityType] = useState<number>(AD_ENTITY_TYPES.USER);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const { setLayoutHeader, setLayoutContentLeft, setLayoutContentIcon, clearLayout } = useTowerLayout();
    
    useEffect(() => {
        setLayoutHeader('Exposures Customization');
        setLayoutContentIcon(
            <Icon className="text-sf-gray-400" icon={Icons.IdentitySearch} size={20} />
        );
        setLayoutContentLeft(
            <Button
                buttonStyle={ButtonStyles.GhostSolid}
                iconRight={Icons.ArrowBack}
                onClick={onBackClick}
            />
        );
        return clearLayout;
    }, [setLayoutHeader, setLayoutContentIcon, setLayoutContentLeft, clearLayout, onBackClick]);

    const closeDrawer = useCallback(() => {
        setIsDrawerOpen(false);
        setDrawerMode(null);
        setSelectedExposure(null);
    }, []);

    const onActionSuccess = useCallback(
        (message) => {
            showAlert(message, 'success');
            refetchSettings();
            closeDrawer();
            refreshInsightsData();
        },
        [refetchSettings, closeDrawer, refreshInsightsData]
    );

    const onSuccess = useCallback(() => {
        onActionSuccess(successMessage);
    }, [onActionSuccess]);

    const onError = useCallback(() => {
        showAlert(errorMessage, 'error');
    }, []);

    const onDeleteSuccess = useCallback(() => {
        onActionSuccess(deleteSuccessMessage);
    }, [onActionSuccess]);

    const onDeleteError = useCallback(() => {
        showAlert(deleteErrorMessage, 'error');
    }, []);

    const { saveExposuresConfigurations, loading: isSaving } = useSaveExposuresConfiguration({
        onSuccess: onSuccess,
        onError
    });

    const { deleteCustomExposures, loading: isDeleting } = useDeleteCustomExposure({
        onSuccess: onDeleteSuccess,
        onError: onDeleteError
    });
    const { saveConfigurationStatus } = useSaveExposuresConfigurationStatus({ onSuccess, onError });

    const categories = useMemo(() => {
        const uniqueCategories = Array.from(new Set(exposuresConfigurations.map((config) => config.category)));
        return [ALL_CATEGORY, ...uniqueCategories];
    }, [exposuresConfigurations]);

    const searchLower = search.trim().toLowerCase();

    const filteredExposures = useMemo(
        () =>
            exposuresConfigurations.filter((e) => {
                if (showOnlyActive && !e.isActive) return false;
                if ((categoryFilter?.length ?? 0) > 0 && !categoryFilter!.includes(e.category)) return false;
                if (!searchLower) return true;
                return e.displayName?.toLowerCase().includes(searchLower);
            }),
        [exposuresConfigurations, showOnlyActive, categoryFilter, searchLower]
    );

    const newCustomExposureConfig: CustomExposureConfig | null = useMemo(() => {
        if (drawerMode !== 'create-custom') return null;
        return buildDefaultCustomExposureConfig(newCustomEntityType);
    }, [drawerMode, newCustomEntityType]);

    const toggleExposure = async (id: string, isActive: boolean) => {
        await saveConfigurationStatus({ id, status: isActive });
    };

    const openCreateCustomDrawer = useCallback((entityType: number) => {
        setDrawerMode('create-custom');
        setNewCustomEntityType(entityType);
        setSelectedExposure(null);
        setIsDrawerOpen(true);
    }, []);

    const openRow = useCallback((row: ExposureConfig | CustomExposureConfig) => {
        if (row.id.endsWith('custom_rule')) {
            setDrawerMode('edit-custom');
        } else {
            setDrawerMode('static');
        }

        setSelectedExposure(row);
        setIsDrawerOpen(true);
    }, []);

    const saveExposureConfig = useCallback(
        async (exposureConfig: SaveExposuresConfigPayload) => {
            if (!exposureConfig) return;
            await saveExposuresConfigurations(exposureConfig);
        },
        [saveExposuresConfigurations]
    );
    const columns = useGetColumns(toggleExposure, openRow);

    return (
        <div className="flex flex-col flex-1 gap-4 h-full">
            <div className="flex flex-none justify-between px-6 mt-2 w-full h-fit">
                <div className="flex flex-grow gap-2 items-center">
                    <ExposuresFilters
                        categories={categories}
                        selectedCategories={categoryFilter ?? []}
                        setSelectedCategories={setCategoryFilter}
                        showOnlyActive={showOnlyActive}
                        setShowOnlyActive={setShowOnlyActive}
                    />
                </div>
                <div className="flex flex-none gap-2 justify-end h-full w-75">
                    <Search onChange={setSearch} text={search} placeholder="Search" border={true} />
                    <SelectList
                        items={CREATE_CUSTOM_DROPDOWN_OPTIONS}
                        onChange={(value) => {
                            if (value) {
                                openCreateCustomDrawer(value as number);
                            }
                        }}
                        triggerProps={{
                            text: 'Create Exposure',
                            buttonStyle: ButtonStyles.Primary
                        }}
                        TriggerComponent={Button}
                    />
                </div>
            </div>

            <div className="flex-grow flex-shrink px-6 w-full h-1" ref={tableRef}>
                <Table
                    loading={false}
                    columns={columns}
                    bodyHeight={tableHeight ? tableHeight - 56 : 0}
                    rows={filteredExposures}
                    rowMargin={8}
                    rowClassName={`rounded-lg mb-4 border ${isFetching ? 'animate-pulse' : ''}`}
                    EmptyStateProps={{
                        className: 'w-full h-max',
                        title: !search ? 'No Exposures' : 'No Results',
                        description: !search
                            ? 'No exposure configurations available'
                            : 'You may need to adjust your search',
                        icon: !search ? EmptyStateIcons.TaskDone : EmptyStateIcons.NoResults
                    }}
                    tableContainerClassName="w-full h-full"
                    keyExtractor={(row) => row.id}
                />
            </div>

            <StaticExposureConfigurationDrawer
                isOpen={isDrawerOpen && drawerMode === 'static' && selectedExposure !== null}
                onClose={closeDrawer}
                exposureConfig={selectedExposure as StaticExposureCustomConfig}
                isSaving={isSaving}
                onSave={saveExposureConfig}
            />

            <CustomExposureConfigurationDrawer
                isOpen={(isDrawerOpen && drawerMode === 'create-custom') || drawerMode === 'edit-custom'}
                onClose={closeDrawer}
                onSave={saveExposureConfig}
                onDelete={deleteCustomExposures}
                isSaving={isSaving || isDeleting}
                isNew={drawerMode === 'create-custom'}
                customExposureConfig={
                    drawerMode === 'edit-custom'
                        ? (selectedExposure as CustomExposureConfig)
                        : (newCustomExposureConfig as CustomExposureConfig)
                }
            />
        </div>
    );
};

export default ExposuresConfigurationPage;
